import { addRatEventWithData } from '../../../utils/rat';
import * as webStorage from '@com.rakuten.esd/myr-web-storage';
import { SocialProviderName } from '../types/socialAccounts.types';

/**
 * Sends RAT events for social account linkage actions
 * Format: "Unlink [provider] [client_id]" or "Unlink confirm [provider] [client_id]"
 */
function sendSocialAccountRatEvent(
    eventAction:
        | 'unlink'
        | 'unlink_confirm'
        | 'unlink_cancel'
        | 'unlink_successful'
        | 'unlink_failed',
    provider: SocialProviderName,
    unlinkTargetId: string,
): void {
    try {
        const clientId = webStorage.getActiveClientId() || 'UNDEFINED';

        // Convert provider name to lowercase for event name
        const providerLowerCase = provider.toLowerCase();

        // Format event name according to screenshot: "Unlink [provider] [client_id]"
        const eventNameParts = eventAction.split('_');
        const formattedEventName = [
            ...eventNameParts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)),
            providerLowerCase,
            unlinkTargetId,
        ].join(' ');

        addRatEventWithData({
            eventType: 'click',
            cpData: {
                client_id: clientId,
                event_category: 'social account linkage',
                event_name: formattedEventName,
            },
        });
    } catch (error) {
        console.error('Error sending social account RAT event:', error);
    }
}

export const SocialAccountRatEvents = {
    /**
     * Track when user clicks the unlink button on a social provider (opens modal)
     * Sends: "Unlink [provider] [unlinked_client_id]"
     */
    trackUnlinkClick: (provider: SocialProviderName, unlinkTargetId: string) => {
        sendSocialAccountRatEvent('unlink', provider, unlinkTargetId);
    },

    /**
     * Track when user confirms unlinking in the modal (clicks Unlink button in modal)
     * Sends: "Unlink confirm [provider] [unlinked_client_id]"
     */
    trackUnlinkConfirm: (provider: SocialProviderName, unlinkTargetId: string) => {
        sendSocialAccountRatEvent('unlink_confirm', provider, unlinkTargetId);
    },

    /**
     * Track when user cancels unlinking in the modal
     * Sends: "Unlink cancel [provider] [unlinked_client_id]"
     */
    trackUnlinkCancel: (provider: SocialProviderName, unlinkTargetId: string) => {
        sendSocialAccountRatEvent('unlink_cancel', provider, unlinkTargetId);
    },

    /**
     * Track when unlink is successful
     * Sends: "Unlink successful [provider] [unlinked_client_id]"
     */
    trackUnlinkSuccessful: (provider: SocialProviderName, unlinkTargetId: string) => {
        sendSocialAccountRatEvent('unlink_successful', provider, unlinkTargetId);
    },

    /**
     * Track when unlink fails
     * Sends: "Unlink failed [provider] [unlinked_client_id]"
     */
    trackUnlinkFailed: (provider: SocialProviderName, unlinkTargetId: string) => {
        sendSocialAccountRatEvent('unlink_failed', provider, unlinkTargetId);
    },
};
